import React, { useCallback, useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useCurrentUser, useAuthActions } from '../../../modules/auth/hooks';
import http from 'utils/http';
import useSnackbars from '../Snackbar/use-snackbars';
import { useModal } from '../Modal/useModal';
import ConsentModalContent from './ConsentModalContent';
import type { LegalNoticeItem } from './consentTypes';
import { useSelector } from 'react-redux';
import { selectSwitchedUsername } from '../../../modules/auth/selectors';

const CONSENT_STATUS_QUERY_KEY = ['consent', 'status'];
const CONSENT_API = {
  status: '/api/consent/status',
  record: '/api/consent/record',
};

type ConsentStatusResponse = {
  hasConsent: boolean;
  subjectId?: string;
  timestamp?: string;
  requiredLegalNotices: LegalNoticeItem[];
};

/**
 * Fetches consent status from backend (user resolved from token).
 * When consent is required, opens the consent modal via useModal.
 * Renders null; modal is opened in useEffect when showModal becomes true.
 */
const Consent = () => {
  const user = useCurrentUser();
  const switchedUser = useSelector(selectSwitchedUsername);
  const { logout } = useAuthActions();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const snackbars = useSnackbars();
  const modal = useModal();
  const [recordedThisSession, setRecordedThisSession] = useState(false);

  const { data: status, isSuccess } = useQuery({
    queryKey: CONSENT_STATUS_QUERY_KEY,
    queryFn: async () => {
      const res = await http.get(CONSENT_API.status);
      return res as unknown as ConsentStatusResponse;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  const requiredLegalNotices = status?.requiredLegalNotices ?? [];

  const getNoticeByIdentifier = useCallback(
    (identifier: string) =>
      requiredLegalNotices.find((n) => n.identifier === identifier),
    [requiredLegalNotices],
  );

  const recordMutation = useMutation({
    mutationFn: async (legalNotices: LegalNoticeItem[]) => {
      await http.post(CONSENT_API.record, { legalNotices });
    },
    onSuccess: () => {
      setRecordedThisSession(true);
      void queryClient.invalidateQueries({
        queryKey: CONSENT_STATUS_QUERY_KEY,
      });
    },
    onError: () => {
      snackbars.add({
        message: t('legal_notices_dialog.save_error'),
        variant: 'error',
      });
    },
  });

  const handleAccept = useCallback(() => {
    if (requiredLegalNotices.length > 0) {
      recordMutation.mutate(requiredLegalNotices);
    }
  }, [requiredLegalNotices, recordMutation]);

  const handleDecline = useCallback(() => {
    logout();
  }, [logout]);

  const canAccept = requiredLegalNotices.length > 0;

  const showModal =
    switchedUser === null &&
    !!user &&
    isSuccess &&
    status !== undefined &&
    !status.hasConsent &&
    !recordedThisSession;

  useEffect(() => {
    if (showModal) {
      modal.open(
        ({ close }) => (
          <ConsentModalContent
            requiredLegalNotices={requiredLegalNotices}
            getNoticeByIdentifier={getNoticeByIdentifier}
            onAccept={() => {
              handleAccept();
              close();
            }}
            onDecline={() => {
              handleDecline();
              close();
            }}
            canAccept={canAccept}
          />
        ),
        {
          title: t('legal_notices_dialog.title'),
          disableEscapeKeyDown: true,
          disableClose: true,
          disableBackdropClose: true,
          size: 'large',
        },
      );
    }
  }, [showModal]);

  return null;
};

export default Consent;
